import type { createEventBus } from '@unifyapps/carbon/emitter';
import { type RefObject, useEffect, useRef } from 'react';
import type { InterfaceResourceProviderRefType } from '../components/page/InterfacePageProviders/types';
import type { DependencyGraphStoreRefType } from '../context/DependencyGraphProvider/types';
import type { DataSourceRecordStoreRefType } from '../stores/DataSourceRecordStore/types';
import type { GlobalStateStoreRefType } from '../stores/GlobalStateStore';
import type { InterfaceStoreRefType } from '../stores/InterfaceStore';
import type { InterfaceEventBusType } from '../types/eventBus';

export const useStoreRefs = ({
  interfaceResourceProviderRef,
  onInterfaceResourceProviderRefInit,
  eventBus: interfaceEventBus,
}: {
  onInterfaceResourceProviderRefInit?: () => void;
  interfaceResourceProviderRef?: RefObject<InterfaceResourceProviderRefType | undefined>;
  eventBus?: ReturnType<typeof createEventBus<InterfaceEventBusType>>;
} = {}) => {
  const globalStateStoreRef = useRef<GlobalStateStoreRefType | undefined>(undefined);
  const interfaceStoreRef = useRef<InterfaceStoreRefType | undefined>(undefined);
  const dependencyGraphStoreRef = useRef<DependencyGraphStoreRefType | undefined>(undefined);
  const dataSourceRecordStoreRef = useRef<DataSourceRecordStoreRefType | undefined>(undefined);

  useEffect(() => {
    if (interfaceResourceProviderRef) {
      interfaceResourceProviderRef.current = {
        emitInboundEvent: (eventType, eventPayload) => {
          const pageScopeKey = interfaceStoreRef.current?.getPageScopeKey();
          if (!pageScopeKey) {
            return;
          }

          interfaceEventBus?.publish('onInboundPageEvent', {
            pageScopeKey,
            eventType,
            eventPayload,
          });
        },
        updateBlocks: (params) => {
          // Call updateBlocks on both stores
          // TODO: when the blocks are added, they need to have the dependsOn etc updated

          if (params.added.length > 0 || params.changed.length > 0 || params.deleted.length > 0) {
            globalStateStoreRef.current?.updateBlocks(params);
            interfaceStoreRef.current?.updateBlocks(params);
            dependencyGraphStoreRef.current?.shouldRebuildGraph();
          }
        },
        updatePageVariables: (params) => {
          if (params.added.length > 0 || params.changed.length > 0 || params.deleted.length > 0) {
            globalStateStoreRef.current?.updatePageVariables(params);
            interfaceStoreRef.current?.updatePageVariables?.(params);
            dependencyGraphStoreRef.current?.shouldRebuildGraph();
          }
        },
        updateDataSources: (params) => {
          if (!dataSourceRecordStoreRef.current) return;
          if (!params.added.length && !params.changed.length && !params.deleted.length) return;

          dataSourceRecordStoreRef.current.updateDataSources(params);
          dependencyGraphStoreRef.current?.shouldRebuildGraph();
        },
        setPageVariables: (variables) => {
          globalStateStoreRef.current?.setPageVariables?.(variables);
        },
        getBlockRef: (blockId) => globalStateStoreRef.current?.getBlockRef?.(blockId),
      };
      onInterfaceResourceProviderRefInit?.();
    }
  }, [interfaceEventBus, interfaceResourceProviderRef, onInterfaceResourceProviderRefInit]);

  return {
    globalStateStoreRef,
    dependencyGraphStoreRef,
    interfaceStoreRef,
    dataSourceRecordStoreRef,
  };
};
